<template>
  <div class="Features">
    <div class="max-w-page lg:px-0 px-8 mx-auto">
      <h2 class="lg:text-5xl lg:w-128 mx-auto mb-4 text-4xl font-bold leading-tight text-center" style="text-wrap: balance">Make sense of your training</h2>
      <p class="lead lg:w-128 mx-auto text-center">Science-backed analytics that turn your rides into clear guidance — and proof you're getting faster.</p>
    </div>

    <!-- Tabbed showcase: pills auto-advance (driven by each pill's progress animation), pause on hover. -->
    <div
      ref="carousel"
      class="Features__carousel max-w-page mx-auto"
      :class="{ 'is-running': inView && !paused }"
      @mouseenter="paused = true"
      @mouseleave="paused = false"
    >
      <div class="Features__pills">
        <button v-for="(item, i) in features" :key="i" type="button" class="Features__pill" :class="{ 'is-active': i === activeIndex }" @click="select(i)">
          {{ item.tab }}
        </button>

        <!-- Circular auto-advance loader at the end of the pills; the ring filling advances the
             slide (keyed to activeIndex so it restarts each time). Hover pauses it. -->
        <svg class="Features__timer" viewBox="0 0 36 36" aria-hidden="true">
          <circle class="Features__timer-track" cx="18" cy="18" r="16" />
          <circle :key="activeIndex" class="Features__timer-fill" cx="18" cy="18" r="16" @animationend="next" />
        </svg>
      </div>

      <div class="Features__stage">
        <transition name="feature">
          <div :key="activeIndex" class="Features__panel lg:flex-row lg:gap-x-16 flex flex-col items-center">
            <div class="Features__media">
              <img
                :src="active.imageUrl | imageUrl"
                :class="active.shadow ? 'border border-gray-200 shadow-sm rounded' : ''"
                class="Features__img"
                :alt="active.title"
              />
            </div>
            <div class="lg:w-96 lg:mt-0 w-full mt-8">
              <h3 class="md:text-3xl lg:text-4xl text-2xl leading-snug" style="text-wrap: balance">{{ active.title }}</h3>
              <div class="text-muted md:text-lg mt-4 leading-relaxed" v-html="active.description"></div>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
const image1 = require('images/features/training-load-with-readiness.jpg')
const image2 = require('images/features/form-and-fitness.jpg')
const image3 = require('images/features/calendar.jpg')
const image4 = require('images/features/power-analysis.png')
const image7 = require('images/features/interval-training-analysis.jpg')
const image8 = require('images/features/activity-analysis-detail.jpg')
const image10 = require('images/features/time-based-analysis.jpg')
const imageAerobicEfficiency = require('images/help/aerobic-base.jpg')

export default {
  data() {
    return {
      activeIndex: 0,
      paused: false,
      inView: false,
      observer: null,
      features: [
        {
          tab: 'Daily Readiness',
          title: 'The session that fits today, ready when you are',
          description:
            'Open FormBeat each morning and see the session that fits today — a readiness-based workout suggestion matched to how you\'re actually recovering, with your <a class="underline" href="https://www.formbeat.com/training-glossary/tss">training load</a>, form and fitness all at a glance. Training for something? Set up an adaptive plan to your event or focus (beta) and the day\'s session comes from it.',
          imageUrl: image1
        },
        {
          tab: 'Form & Fitness',
          title: 'Predictive Form & Fitness',
          description:
            'Our Form & Fitness chart tracks your level of fitness and fatigue and predicts your cycling form to help you schedule your workouts and plan for a race.',
          imageUrl: image2,
          shadow: true
        },
        {
          tab: 'Aerobic Base',
          title: 'Aerobic Efficiency',
          description:
            "See if your endurance training is actually working. FormBeat tracks your watts per heartbeat over time — a rising trend means you're producing more power at the same heart rate. Same effort, more output.",
          imageUrl: imageAerobicEfficiency,
          shadow: true
        },
        {
          tab: 'FTP & Power Profile',
          title: 'FTP & Power Profile Analysis',
          description:
            "Track all power metrics over time – see how your power curve, peak power levels, FTP, Critical power, Anaerobic Capacity, W' and W/kg improve over time and compare to the riders ranging from novice to the world-class, pro road cyclists.",
          imageUrl: image4,
          shadow: true
        },
        {
          tab: 'Training Calendar',
          title: 'Training Calendar',
          description:
            "The calendar view aggregates the most important metrics of your training and makes it easy to compare each week's impact on your form and fitness.",
          imageUrl: image3,
          shadow: true
        },
        {
          tab: 'Activity Analysis',
          title: 'Activity Analysis',
          description:
            'State-of-the-art activity analysis dives into the details of your workouts. Quickly jump into your power and heart rate peaks and analyze interval workouts.',
          imageUrl: image8,
          shadow: true
        },
        {
          tab: 'Performance Tracking',
          title: 'Performance Tracking',
          description:
            'See how your power, heart rate, FTP, or average speed improved over the past weeks, months, or years. Use our training and performance analysis to see your fitness improve over time.',
          imageUrl: image10
        },
        {
          tab: 'Interval Training',
          title: 'Interval Training',
          description:
            'Use laps & intervals to analyze the structure of your interval training. Interval workouts include an alternating pattern of high-intensity efforts and easier recovery riding.',
          imageUrl: image7,
          shadow: true
        }
      ]
    }
  },

  computed: {
    active() {
      return this.features[this.activeIndex]
    }
  },

  mounted() {
    // Only run the auto-advance while the carousel is on screen — so it starts on slide 1 and
    // doesn't quietly cycle through while the visitor is still up at the hero.
    this.observer = new IntersectionObserver(([entry]) => (this.inView = entry.isIntersecting), { threshold: 0.3 })
    this.observer.observe(this.$refs.carousel)
  },

  beforeDestroy() {
    if (this.observer) this.observer.disconnect()
  },

  methods: {
    select(i) {
      this.activeIndex = i
    },

    // Fired by the active pill's progress animation ending — advances to the next feature,
    // which restarts the animation on the newly-active pill. Hover pauses the animation
    // (animation-play-state), so animationend never fires while paused — no separate timer needed.
    next() {
      this.activeIndex = (this.activeIndex + 1) % this.features.length
    }
  }
}
</script>

<style lang="postcss" scoped>
.Features {
  @apply w-full mx-auto;
}

.Features__carousel {
  @apply px-8 mt-10;
}

/* ---- Pills ---- */
.Features__pills {
  @apply gap-x-3 gap-y-3 flex flex-wrap items-center justify-center mb-10;
}

.Features__pill {
  @apply focus:outline-none px-4 py-2 text-sm font-semibold text-gray-800 border border-gray-500 rounded-full cursor-pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.Features__pill:hover {
  @apply text-gray-900 bg-gray-100;
}

.Features__pill.is-active {
  @apply text-white;
  background: black;
}

/* Circular auto-advance loader at the end of the pills; the ring filling advances the slide. */
.Features__timer {
  @apply flex-shrink-0;
  width: 1.5rem;
  height: 1.5rem;
  transform: rotate(-90deg); /* start the sweep at 12 o'clock */
}
.Features__timer-track {
  fill: none;
  stroke: rgba(0, 0, 0, 0.1);
  stroke-width: 3;
}
.Features__timer-fill {
  fill: none;
  stroke: #9ca3af;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-dasharray: 100.53; /* 2π·16 */
  stroke-dashoffset: 100.53;
  animation: feature-timer 5s linear forwards;
  /* Paused by default — only runs while in view and not hovered (see is-running). */
  animation-play-state: paused;
}
.Features__carousel.is-running .Features__timer-fill {
  animation-play-state: running;
}
@keyframes feature-timer {
  to {
    stroke-dashoffset: 0;
  }
}

/* ---- Stage: fixed media height keeps the panel height stable so the crossfade can't jump. ---- */
.Features__stage {
  @apply relative;
}

.Features__media {
  @apply flex items-center justify-center w-full;
  height: 16rem;

  @screen lg {
    @apply w-2/3;
    height: 30rem;
  }
}

.Features__img {
  @apply mx-auto;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

/* Smooth crossfade: the leaving slide overlaps (absolute) so there's no blank gap or height pop. */
.feature-enter-active,
.feature-leave-active {
  transition: opacity 0.45s ease;
}
.feature-enter,
.feature-leave-to {
  opacity: 0;
}
.feature-leave-active {
  @apply absolute top-0 left-0 w-full;
}
</style>
